// STEP orb (this task) — the five ORDER rows. Spec: docs/redesign/ORB_SPEC.md.
import React, { useState } from 'react';
import { View } from 'react-native';
import { clock, dateKeyOf, trunc } from './time';
import { Big, Btn, DoneOrNot, Field, Head, Line, Mark, Quiet, Txt, useK } from './ui';
import { NOT_SET, chainOf, eventsFor, joinLine, slotState } from './shared';
import { mmss, type Runner } from './runner';
import type { OrbItem, ProtocolLite, RoutineStep, RowCtx, RowSet, SwipeAction } from './types';

export interface StepExtra {
  step: OrbItem;
  runner: Runner;
  protocol: ProtocolLite | null;
  routine: RoutineStep[] | null;
  /** Steps before this one in its TKNE project (real gates), in order. */
  prior: { title: string; done: boolean }[];
  /** Sub-steps TKNE already holds for it — offered as the first draft of a routine. */
  subs: string[];
  serves: { today: string | null; aim: string | null; goal: string | null; endeavour: string | null };
  onDone: () => void;
}

function RoutineWriter({ initial, onSave, busy }: { initial: string; onSave: (lines: string[]) => Promise<boolean>; busy: boolean }) {
  const k = useK();
  const [v, setV] = useState(initial);
  return (
    <View style={{ alignSelf: 'stretch', gap: 4 * k }}>
      <Txt faint style={{ fontStyle: 'italic', textAlign: 'center' }}>No routine yet — one step per line (“Set colours 10” = 10 min)</Txt>
      <Field value={v} onChangeText={setV} multiline placeholder={'Set colours 10\nFix spacing 15'} />
      <View style={{ alignItems: 'center' }}>
        <Btn label="Save routine" tone="gold" disabled={busy || !v.trim()} onPress={() => void onSave(v.split('\n'))} />
      </View>
    </View>
  );
}

export function stepRows(c: RowCtx, x: StepExtra): RowSet {
  const { acts, data, now } = c;
  const { step, runner, protocol, routine } = x;
  const isToday = step.date === dateKeyOf(now);
  const hasRoutine = !!routine && routine.length > 0;
  const kyrsTarget: SwipeAction = { kind: 'tool', tool: 'kyrs', opts: step.taskId ? { initialTaskId: step.taskId } : undefined, label: 'the task in KYRS' };
  const themisTarget = (label: string): SwipeAction => ({ kind: 'tool', tool: 'themis', opts: protocol ? { initialProtocolId: protocol.id } : undefined, label });

  // ---- O · why, and what counts as done ---------------------------------
  // serves.today is the day's objective — skip it when it is this very task.
  const today = x.serves.today && x.serves.today !== step.title ? x.serves.today : null;
  const chain = chainOf([['', today], ['Aim', x.serves.aim], ['Goal', x.serves.goal], ['Endeavour', x.serves.endeavour]]);
  const doneWhen = hasRoutine ? `all ${routine!.length} routine steps finished` : x.subs.length ? `its ${x.subs.length} TKNE ${x.subs.length === 1 ? 'sub-step is' : 'sub-steps are'} checked off` : null;
  const oLine = chain ? `Serves ${chain}` : `Purpose: ${NOT_SET}`;
  const oAnswer = [`Supports: ${chain ?? NOT_SET}`, `Done when: ${doneWhen ?? NOT_SET}`];

  // ---- R · everything it needs --------------------------------------------
  const checks: { ok: boolean; text: string; miss: string }[] = [];
  if (step.source === 'step' && !step.taskId) {
    checks.push({ ok: false, text: 'Not on a day’s plan yet', miss: 'a place on the plan' });
  } else {
    const s = slotState(step, eventsFor(c, step.date));
    checks.push({ ok: s.ready, text: s.ready ? `Time slot${step.start ? ` ${clock(step.start)}` : ''}` : s.why === 'no time slot' ? 'No time slot yet' : `Slot ${s.why}`, miss: s.why === 'no time slot' ? 'a time slot' : (s.why ?? 'a time slot') });
  }
  const undone = x.prior.filter((p) => !p.done);
  for (const p of undone.slice(0, 2)) checks.push({ ok: false, text: `Before it: ${p.title}`, miss: `“${trunc(p.title, 18)}” first` });
  if (x.prior.length && !undone.length) checks.push({ ok: true, text: 'Earlier steps are done', miss: '' });
  const missing = checks.find((m) => !m.ok);
  const rLine = step.completed ? 'Done · nothing more needed' : missing ? `NOT READY · missing ${missing.miss}` : 'READY';
  const rAnswer = step.completed
    ? ['Done · nothing more needed']
    : [missing ? `NOT READY — missing ${missing.miss}` : 'READY — everything it needs is in place', ...checks.map((m) => `${m.ok ? '✓' : '✗'} ${m.text}`)];

  // ---- D · approach -------------------------------------------------------
  const n = routine?.length ?? 0;
  const at = Math.min(runner.idx, Math.max(0, n - 1));
  const deps = undone.length ? `Depends on: ${undone.map((p) => p.title).join(', ')}` : x.prior.length ? 'Depends on: earlier steps — all done' : null;
  let dLine: string;
  let dAnswer: string[];
  if (hasRoutine) {
    const nextUp = routine!.slice(at, at + 3).map((s) => s.title);
    const leftMin = routine!.slice(at).reduce((m, s) => m + s.minutes, 0);
    dLine = `Next: ${nextUp.join(' → ')}`;
    dAnswer = [`Next: ${nextUp.join(' → ')}`, `${n - at} of ${n} steps left · ${leftMin} min`, ...(deps ? [deps] : [])];
  } else if (x.subs.length) {
    dLine = `Next: ${x.subs.slice(0, 3).join(' → ')}`;
    dAnswer = [`Next: ${x.subs.slice(0, 3).join(' → ')}`, `${x.subs.length} sub-steps in TKNE · no THEMIS routine yet`, ...(deps ? [deps] : [])];
  } else {
    dLine = `Approach: ${NOT_SET} · write the steps`;
    dAnswer = [`Approach: ${NOT_SET}`, 'No routine or sub-steps yet — write the steps below', ...(deps ? [deps] : [])];
  }
  const win = hasRoutine ? routine!.slice(Math.max(0, Math.min(at - 1, n - 4)), Math.max(0, Math.min(at - 1, n - 4)) + 4) : [];
  const winStart = hasRoutine ? Math.max(0, Math.min(at - 1, n - 4)) : 0;

  // ---- E · doing now ------------------------------------------------------
  const nowTitle = hasRoutine ? runner.cur?.title ?? step.title : step.title;
  const fullSecs = (hasRoutine ? runner.cur?.minutes ?? step.minutes : step.minutes) * 60;
  const runState = step.completed ? 'done' : runner.finished ? 'finished' : runner.running ? 'running' : runner.left < fullSecs ? 'paused' : 'not started';
  const eLine = step.completed ? `${trunc(step.title, 26)} · done` : joinLine([nowTitle, runner.finished ? null : `${mmss(runner.left)} left`, runState]);
  const eAnswer = step.completed
    ? [`Done: ${step.title}`]
    : [`Now: ${nowTitle}`, `Timer: ${runner.finished ? 'finished' : `${mmss(runner.left)} left · ${runState}`}`, ...(hasRoutine ? [`Step ${at + 1} of ${n}`] : [])];

  // ---- R · what happened, what changes ------------------------------------
  const canWrite = !!(step.rowId || step.taskId);
  const canReason = !!(step.rowId || (step.taskId && isToday));
  const outcome = step.completed ? 'Done' : step.missed ? `Not done: ${step.missed}` : 'Open — no outcome yet';
  const remaining = step.completed ? 'nothing' : hasRoutine ? `${runner.finished ? 0 : n - at} of ${n} routine steps` : `${step.minutes} min planned`;
  const fLine = joinLine([outcome, !step.completed && `${remaining} left`]);
  const fAnswer = [`Outcome: ${outcome}`, `Remaining: ${remaining}`, `Change: ${step.missed && !step.completed ? `deal with “${step.missed}”` : NOT_SET}`];

  return {
    orient: {
      question: 'Why am I doing this, and what counts as done?',
      line: oLine,
      answer: oAnswer,
      tone: chain ? undefined : 'quiet',
      swipe: { kind: 'tool', tool: 'atlas', label: 'ATLAS' },
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 5, alignItems: 'center' }}>
          <Head>{step.title}</Head>
          {x.serves.today ? (
            <Txt soft style={{ textAlign: 'center' }} lines={2}>
              serves today: {x.serves.today}
            </Txt>
          ) : null}
          {x.serves.aim ? (
            <Txt soft style={{ textAlign: 'center' }} lines={2}>
              serves the aim: {x.serves.aim}
            </Txt>
          ) : null}
          {!x.serves.today && !x.serves.aim ? <Quiet>Nothing above it yet — set the aim in ORACLE.</Quiet> : null}
        </View>
      ),
    },
    regulate: {
      question: 'Do I have everything I need?',
      line: rLine,
      answer: rAnswer,
      tone: step.completed ? 'ok' : missing ? 'warn' : 'ok',
      swipe: kyrsTarget,
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 3 }}>
          {checks.map((m, i) => (
            <Line key={i}>
              <Mark ok={m.ok} />
              <Txt lines={2} style={{ flex: 1 }}>{m.text}</Txt>
            </Line>
          ))}
          <View style={{ alignItems: 'center', marginTop: 3 }}>
            <Btn label="+ Add what this needs" onPress={() => acts.go(kyrsTarget)} />
          </View>
        </View>
      ),
    },
    direct: {
      question: 'What’s my approach?',
      line: dLine,
      answer: dAnswer,
      tone: hasRoutine || x.subs.length ? undefined : 'quiet',
      swipe: themisTarget('THEMIS protocol'),
      widget: hasRoutine ? (
        <View style={{ alignSelf: 'stretch', gap: 3 }}>
          {win.map((s, i) => {
            const idx = winStart + i;
            const cur = idx === at;
            return (
              <Line key={s.id}>
                <Txt faint w={16}>{idx + 1}</Txt>
                <Txt lines={1} soft={!cur} style={{ flex: 1, fontWeight: cur ? '800' : '500' }}>{s.title}</Txt>
                <Txt faint>{s.minutes}m</Txt>
              </Line>
            );
          })}
          {n > win.length ? <Txt faint style={{ textAlign: 'center' }}>{n} steps in all</Txt> : null}
        </View>
      ) : (
        <RoutineWriter key={step.key} initial={x.subs.join('\n')} busy={acts.busy} onSave={(lines) => acts.makeRoutine(step.title, lines)} />
      ),
    },
    execute: {
      question: 'What am I doing now?',
      line: eLine,
      answer: eAnswer,
      swipe: themisTarget('THEMIS runner'),
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 5, alignItems: 'center' }}>
          <Head>{hasRoutine ? (runner.finished ? 'Routine complete' : runner.cur?.title ?? step.title) : step.title}</Head>
          <Big>{runner.finished ? '✓' : mmss(runner.left)}</Big>
          {hasRoutine ? (
            <Txt faint style={{ textAlign: 'center' }}>step {at + 1} of {n}</Txt>
          ) : null}
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <Btn label={runner.running ? 'Pause' : 'Play'} tone="gold" disabled={runner.finished} onPress={runner.running ? runner.pause : runner.play} />
            {hasRoutine ? (
              <>
                <Btn label={at + 1 >= n ? 'Finish' : 'Next'} disabled={runner.finished} onPress={runner.next} />
                <Btn label="Repeat" disabled={runner.finished} onPress={runner.repeat} />
              </>
            ) : (
              <Btn label="Done" tone={canWrite ? 'ok' : 'ghost'} disabled={!canWrite || acts.busy} onPress={x.onDone} />
            )}
          </View>
        </View>
      ),
    },
    revise: {
      question: 'What happened, and what needs to change?',
      line: fLine,
      answer: fAnswer,
      tone: step.completed ? 'ok' : step.missed ? 'warn' : 'quiet',
      swipe: { kind: 'tool', tool: 'metis', label: 'METIS' },
      widget: canWrite ? (
        <DoneOrNot completed={step.completed} missed={step.missed} reasons={data.missReasons} canNot={canReason} onDone={x.onDone} onNot={(reason) => acts.missed(step, reason)} />
      ) : (
        <View style={{ alignSelf: 'stretch', gap: 6, alignItems: 'center' }}>
          <Quiet>This step isn’t on a day’s plan yet, so there is nothing to mark. Pick it for a day first.</Quiet>
          <Btn label="Plan it in KYRS" onPress={() => acts.go(kyrsTarget)} />
        </View>
      ),
    },
  };
}

/** No task to show: the same five questions, each an invite. */
export function emptyStepRows(c: RowCtx, loading: boolean): RowSet {
  const go: SwipeAction = { kind: 'tool', tool: 'kyrs', label: 'KYRS' };
  const line = loading ? 'Finding your place…' : 'Nothing to do yet · plan a task in KYRS';
  const widget = (
    <View style={{ alignSelf: 'stretch', gap: 6, alignItems: 'center' }}>
      <Quiet>{loading ? 'Finding your place…' : 'No task is lined up for this step. Pick one for the day and it appears here.'}</Quiet>
      {loading ? null : <Btn label="Plan it in KYRS" onPress={() => c.acts.go(go)} />}
    </View>
  );
  const mk = (question: string, swipe: SwipeAction) => ({ question, line, answer: [loading ? line : `${NOT_SET} — no task is lined up`], tone: 'quiet' as const, widget, swipe });
  return {
    orient: mk('Why am I doing this, and what counts as done?', { kind: 'tool', tool: 'atlas', label: 'ATLAS' }),
    regulate: mk('Do I have everything I need?', go),
    direct: mk('What’s my approach?', { kind: 'tool', tool: 'themis', label: 'THEMIS protocol' }),
    execute: mk('What am I doing now?', { kind: 'tool', tool: 'themis', label: 'THEMIS runner' }),
    revise: mk('What happened, and what needs to change?', { kind: 'tool', tool: 'metis', label: 'METIS' }),
  };
}
